import { defineRecipe } from '@chakra-ui/react';
import {
  readonlyFieldStyle,
  sharedFieldBase,
  unsavedFieldStyle,
} from './Input';

/**
 * Textarea — mirrors the Input recipe's decisions: focus signal is the halo
 * only, resting border stays its `border.default` gray. The `outline`
 * variant is defined explicitly (not derived from Input's `outline`, which
 * carries Input-only sizing) so it cleanly wins over Chakra's own built-in
 * `outline` variant (which would otherwise inject `focusBorderColor:
 * blue.500` and a 1px box-shadow halo) when this config merges with
 * defaultConfig.
 */

const base = {
  ...sharedFieldBase,
  borderRadius: 'sm',
  paddingY: '8px',
  minHeight: '80px',
  lineHeight: 'short',
  verticalAlign: 'top',
};

export const textareaTheme = defineRecipe({
  base,
  variants: {
    // Declared (but empty) so `defaultVariants` below can reference it — the
    // built-in textarea recipe's own `sm` size still supplies the styles.
    size: {
      sm: {},
    },
    variant: {
      outline: {
        // Longhands, not the `border: '1px solid'` shorthand: v3 merges
        // `base` + variant into one object, so the shorthand would land after
        // base's `borderColor` and reset it to `currentColor` (fg.primary).
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default',
        bg: 'bg.surface',
        color: 'fg.primary',
        fontWeight: 'normal',
        paddingY: '8px',
        minHeight: '80px',
        lineHeight: 'short',
        verticalAlign: 'top',
        borderRadius: 'sm',
        transition:
          'border-color 120ms, box-shadow 120ms, background-color 120ms',
        // Clears the built-in's near-black `:focus-visible` outline — see Input.ts.
        focusVisibleRing: 'none',
        _hover: { borderColor: 'border.default' },
        _disabled: {
          cursor: 'not-allowed',
          color: 'fg.muted',
          background: 'bg.muted',
          borderColor: 'border.subtle',
          opacity: 1,
        },
        _invalid: {
          borderColor: 'tRed.base',
          color: 'tRed.base',
        },
        // Halo only on focus — no border-color shift, matching the Input recipe.
        _focus: {
          zIndex: 1,
          borderColor: 'border.default',
          boxShadow: 'focus',
          bg: 'bg.surface',
          color: 'fg.primary',
        },
        _focusVisible: {
          zIndex: 1,
          borderColor: 'border.default',
          boxShadow: 'focus',
          bg: 'bg.surface',
          color: 'fg.primary',
        },
        _placeholder: {
          color: 'fg.subtle',
          fontWeight: 'normal',
        },
      },
      readonly: {
        ...readonlyFieldStyle,
        borderRadius: 'sm',
        paddingY: '8px',
        minHeight: '80px',
        lineHeight: 'short',
        verticalAlign: 'top',
      },
      unsaved: {
        ...unsavedFieldStyle,
        borderRadius: 'sm',
        paddingY: '8px',
        minHeight: '80px',
        lineHeight: 'short',
        verticalAlign: 'top',
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'outline',
  },
});

export default textareaTheme;
